import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, Heart, Scales } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { favorites, compare, useLocalList } from "@/lib/storage";
import { useLang } from "@/lib/lang";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const favs = useLocalList(favorites);
  const cmp = useLocalList(compare);
  const { t } = useLang();
  const location = useLocation();
  // Nav sits transparent-on-hero on the home page only. Everywhere else it's a
  // solid white bar with the classic contrast. On home we swap to solid the
  // moment the user scrolls 60px past the hero so the nav stays legible over
  // the white sections below.
  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !open;

  useEffect(() => {
    if (!isHome) { setScrolled(false); return; }
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const linkBase = "text-sm tracking-wide transition-colors";
  const linkColor = transparent
    ? "text-white/90 hover:text-white"
    : "text-neutral-700 hover:text-[#001588]";
  const activeColor = transparent ? "text-white" : "text-[#001588]";
  const iconColor = transparent ? "text-white/90 hover:text-white" : "text-neutral-700 hover:text-[#001588]";
  const toggleColor = transparent ? "text-white" : "text-[#050d19]";

  const links = [
    { to: "/properties", label: t("nav.buy") },
    { to: "/rent", label: t("nav.rent") },
    { to: "/projects", label: t("nav.projects") },
    { to: "/map", label: t("nav.map") },
    { to: "/areas", label: t("nav.communities") },
    { to: "/developers", label: t("nav.developers") },
    { to: "/services", label: t("nav.services") },
    { to: "/journal", label: t("nav.journal") },
    { to: "/agents", label: t("nav.team") },
  ];
  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent
          ? "bg-transparent border-b border-transparent"
          : "bg-white/95 backdrop-blur-md border-b border-[#e5e7eb]"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_penta-realty-uae/artifacts/lk46xfkn_Point%20Penta%20Logo%20png.png"
            alt="Point Penta"
            className={`h-11 w-auto transition-[filter] duration-300 ${transparent ? "brightness-0 invert" : ""}`}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? activeColor : linkColor}`
              }>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher dark={transparent} />
          <Link to="/favorites" className={`relative ${iconColor}`} aria-label={t("nav.favorites")} data-testid="nav-favorites">
            <Heart size={20} />
            {favs.length > 0 && <span className="absolute -top-2 -right-2 bg-[#001588] text-white text-[10px] font-medium w-4 h-4 rounded-full flex items-center justify-center">{favs.length}</span>}
          </Link>
          <Link to="/compare" className={`relative ${iconColor}`} aria-label={t("nav.compare")} data-testid="nav-compare">
            <Scales size={20} />
            {cmp.length > 0 && <span className="absolute -top-2 -right-2 bg-[#001588] text-white text-[10px] font-medium w-4 h-4 rounded-full flex items-center justify-center">{cmp.length}</span>}
          </Link>
          <Link to="/valuation" data-testid="nav-valuation" className={`${linkBase} ${linkColor}`}>{t("nav.valuation")}</Link>
          <Link to="/contact" data-testid="nav-contact" className={`px-5 py-2.5 text-sm tracking-wide transition-colors ${transparent ? "bg-white text-[#001588] hover:bg-white/90" : "bg-[#001588] text-white hover:bg-black"}`}>
            {t("nav.enquire")}
          </Link>
        </div>

        <button onClick={() => setOpen(!open)} className={`lg:hidden p-2 ${toggleColor}`} data-testid="nav-menu-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#e5e7eb] px-6 py-6" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-neutral-700 hover:text-[#001588] tracking-wide" data-testid={`mobile-nav-${l.to.replace("/", "")}`}>
                {l.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-[#e5e7eb]"><LanguageSwitcher /></div>
            <Link to="/favorites" onClick={() => setOpen(false)} className="text-neutral-700 hover:text-[#001588]">{t("nav.favorites")} ({favs.length})</Link>
            <Link to="/compare" onClick={() => setOpen(false)} className="text-neutral-700 hover:text-[#001588]">{t("nav.compare")} ({cmp.length})</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="mt-4 px-5 py-3 bg-[#001588] text-white text-center tracking-wide">{t("nav.enquire")}</Link>
          </div>
        </div>
      )}
    </header>
  );
}
